/**
 * Decléor — comparison table: the range's image follows the cursor along the row (desktop only).
 *
 * Hovering a row reveals that range's product shot and the shot then TRAILS the pointer down the
 * row — the image-trail interaction a luxury menu uses, applied to a comparison table. The row the
 * cursor is on keeps its full ink while the others recede (that dim already ships, in
 * snippets/richtext-blocks-style.liquid); this adds the thing the recession was pointing at.
 *
 * ONE FOLLOWER PER TABLE, NOT ONE PER ROW, and that is the whole feel of it. Only one row can be
 * hovered at a time, so a second follower could never be on screen; but more than that, moving from
 * row 3 to row 4 has to read as the SAME object gliding down and changing its mind, not as one
 * element dying and another being born at rest. Six followers would reset the inertia at every row
 * boundary and the effect would land as a series of pops. So there is one positioned box, six
 * <img> inside it, and a row change is a 160ms crossfade between two children of a box that never
 * stops moving. It also means one rAF loop, one transform and one piece of state for the component.
 *
 * THE INERTIA IS A rAF LERP, NOT A CSS TRANSITION, and the difference is not stylistic.
 * A `transition: transform 300ms` restarts from scratch on every mousemove: each event sets a new
 * end point and the curve begins again at its slow ease-in, so a continuously moving cursor is
 * chased by an element that is permanently in the first 16ms of a 300ms ease — it crawls, then
 * lurches when the cursor stops. What a trail actually needs is a first-order follow: each frame
 * the box closes a fixed FRACTION of the distance that remains, so its speed is proportional to how
 * far behind it is. It sets off the instant the cursor does, runs fastest when it is furthest
 * behind, and decelerates into place on its own. That is one line of maths and it cannot be spelled
 * as a transition.
 *
 * THE FRACTION IS FRAME-RATE CORRECTED. A flat `x += (target - x) * 0.14` is twice as fast on a
 * 120Hz MacBook as on a 60Hz external screen — the same gesture would feel weighted on one and
 * snappy on the other. assets/focus-scroll-pin.js can use a flat lerp because its target is driven
 * by the scroll position and therefore self-corrects; here the target is static between events, so
 * the frame rate IS the duration. `1 - (1 - LERP) ^ (dt / 16.667)` is the same curve expressed per
 * millisecond, so 60Hz and 120Hz feel identical. dt is capped at 64ms so a tab that comes back from
 * the background does not teleport.
 *
 * LERP 0.14 — measured in feel, not in theory: the box is 63% of the way there after ~8 frames
 * (135ms) and visually arrived in about a third of a second. 0.08 is syrup, and the image reads as
 * detached from the cursor; 0.30 is a hard stick with no weight at all, which is what not doing this
 * would have looked like.
 *
 * IT STOPS WHEN IT SETTLES — same discipline as assets/focus-scroll-pin.js, and it needs the same
 * dead band for the same reason. A proportional follow halves its remaining distance forever and
 * never arrives, so without a threshold this becomes a permanent 60fps rAF over a still page. Under
 * 0.2px the residual is assigned exactly and the loop ends. 0.2 rather than that file's 0.5 because
 * this drives a transform rather than an integer scrollLeft — 0.2px is still under half a device
 * pixel on a 2x screen, so nothing can be seen to snap. And the loop is woken by scroll while the
 * cursor is on a row, because Lenis keeps micro-adjusting the page for a beat after the gesture
 * ends and the table slides under a stationary pointer: without that wake the image would come
 * unstuck from the cursor during the settle. Each wake settles again within a frame or two.
 *
 * WHO ENFORCES WHAT
 *   desktop width          the STYLESHEET (`min-width: 900px`), because that is where the table
 *                          stops being a table and becomes a stack of cards. This file never names
 *                          a breakpoint: it asks the DOM whether the follower is displayed, exactly
 *                          as assets/ptable-focus.js asks whether the cells are blocks, so the two
 *                          can never disagree. The media query also keeps `display: none` on phones,
 *                          which is what stops six lazy images being fetched on a phone at all.
 *   a real pointer         BOTH. `(hover: hover) and (pointer: fine)` in the media query, and the
 *                          same query in matchMedia here so a touch device binds no listeners and
 *                          a tap's compatibility mousemove can never latch the image on.
 *   reduced motion         BOTH, and it stands down completely rather than degrading. An element
 *                          that chases the cursor is the exact class of motion the preference is
 *                          about, and the image carries no information the table does not already
 *                          state in words.
 *
 * AT THE EDGES the pointer position is clamped, not released. The box is centred on the cursor, so
 * an unclamped follow would hang half of a 240px image outside the table — and the table is capped
 * at the viewport width, so on a 1500px screen that is a horizontal scrollbar on the whole page
 * (the failure CLAUDE.md documents for `1fr` tracks, arrived at from a different direction). The
 * CENTRE is therefore held inside the component's own box on both axes: the image tracks the cursor
 * exactly across the middle ~80% of a row and eases to a stop near the ends, which reads as the
 * image being held by the table rather than as the follow breaking. Vertically the same clamp keeps
 * it from climbing out over the section above on the first row.
 *
 * No-op without a [data-pt-follow]. Re-binds on shopify:section:load and never binds twice.
 * ES5 IIFE, classic deferred script, no build step — see CLAUDE.md.
 */
(function () {
  'use strict';

  var BOUND = 'data-dc-pth-bound';

  /* Asked once, at load, and deliberately not re-asked: a visitor does not swap a trackpad for a
     touchscreen mid-session, and ptable-focus.js sets the precedent. The WIDTH question is the one
     that genuinely changes while the page is open, and that one is asked of the DOM every time the
     cursor enters the table. */
  if (!window.matchMedia) {
    return;
  }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    return;
  }

  var LERP = 0.14;
  var SNAP = 0.2;
  var STEP = 1000 / 60;

  /* Clear air between the pointer and the nearest edge of the frame. Enough that the cursor is
     plainly beside the image rather than touching it, small enough that the two still read as one
     gesture. */
  var GAP = 28;

  /* How much room the preferred side has to REGAIN before the box flips back to it. Without this
     the image would flicker between the two sides for every pixel of jitter at the threshold; with
     it, coming back from an edge is a deliberate move rather than a twitch. */
  var FLIP = 56;

  var items = [];
  var frame = 0;
  var last = 0;

  function now() {
    return window.performance && window.performance.now ? window.performance.now() : Date.now();
  }

  /* The leave duration is --dc-dur-fast, read off the element rather than restated here, so
     design-polish.liquid stays the one place a duration is written. It is needed in JS for one
     decision only: whether a re-entry should snap to the cursor or continue from where the box is. */
  function readDuration(el, name, fallback) {
    var raw = window.getComputedStyle(el).getPropertyValue(name);
    var value;
    if (!raw) {
      return fallback;
    }
    raw = raw.replace(/\s/g, '');
    value = parseFloat(raw);
    if (Number.isNaN(value)) {
      return fallback;
    }
    if (raw.slice(-2) === 'ms') {
      return value;
    }
    if (raw.slice(-1) === 's') {
      return value * 1000;
    }
    return fallback;
  }

  /* WHERE THE BOX GOES RELATIVE TO THE POINTER — beside it, never under it.
   *
   * Centred on the cursor, the image covered the very sentence the row was there to let you read.
   * So the centre is pushed one half-width plus GAP to one side, and the side is chosen per frame:
   * LEFT by default, because the eye is travelling down the index column on the left and the copy it
   * would otherwise hide is to the right of the pointer.
   *
   * It flips to the right only when the left placement would not fit inside the table — and flips
   * BACK only once the left side has FLIP pixels of room to spare. That asymmetry is the whole point
   * of keeping `side` on the item rather than recomputing it cleanly each frame: a single threshold
   * would sit exactly under a slowly moving cursor and the image would strobe between the two sides.
   *
   * If neither side fits (a table narrower than two images) the clamp below takes over and the box
   * simply rides the edge — at that width there is no placement that is not over something. */
  function sideFor(it, want) {
    /* The pointer-to-far-edge distance of a left placement: half a box to reach the near edge, the
       gap, then the other half. The box fits on the left exactly while `want` covers it. */
    var need = it.halfW * 2 + GAP;

    if (it.side < 0) {
      if (want < need) {
        return 1;
      }
      return -1;
    }
    /* Coming back from the right edge costs FLIP more than leaving did. Note this also returns the
       box to the left near the RIGHT edge, where `want` is large and a right placement would be the
       one hanging outside — so one comparison covers both ends. */
    if (want >= need + FLIP) {
      return -1;
    }
    return 1;
  }

  /* Hold the CENTRE inside the box. If the image is somehow wider than its own component, the range
     inverts — pin to the middle rather than let min beat max and jitter between the two. */
  function clamp(value, half, size) {
    var min = half;
    var max = size - half;
    if (max <= min) {
      return size / 2;
    }
    if (value < min) {
      return min;
    }
    if (value > max) {
      return max;
    }
    return value;
  }

  function wake(it) {
    it.awake = true;
  }

  /* Hundredths of a pixel: enough that nothing can be seen to step, short enough that an unchanged
     position produces an identical string and the write is skipped. A custom-property/style write
     is cheap but it still invalidates style for the subtree — the same accounting setVar() does in
     assets/focus-scroll-pin.js. */
  function write(it) {
    var next =
      'translate3d(' +
      Math.round(it.x * 100) / 100 +
      'px,' +
      Math.round(it.y * 100) / 100 +
      'px,0)';
    if (it.transform !== next) {
      it.transform = next;
      it.follow.style.transform = next;
    }
  }

  /* Put the box exactly on the pointer, now, synchronously. Used for an appearance: `is-on` starts
     the fade in the same event, and a box that fades in at last hover's position — or at the
     component's top-left corner, on the first hover of the page — is the one artefact this effect
     cannot survive. One forced layout, once per appearance. */
  function place(it) {
    var rect = it.host.getBoundingClientRect();
    var want = it.px - rect.left;
    it.side = sideFor(it, want);
    it.x = clamp(want + it.side * (it.halfW + GAP), it.halfW, rect.width);
    it.y = clamp(it.py - rect.top, it.halfH, rect.height);
    write(it);
  }

  function schedule() {
    if (frame) {
      return;
    }
    frame = window.requestAnimationFrame(update);
  }

  function update() {
    frame = 0;

    var time = now();
    var dt = last ? time - last : STEP;
    var alpha;
    var moving = false;
    var i;
    var it;
    var rect;
    var want;
    var tx;
    var ty;

    last = time;
    if (dt > 64) {
      dt = 64;
    }
    /* The flat fraction, re-expressed per millisecond. At dt = 16.667 this is exactly LERP. */
    alpha = 1 - Math.pow(1 - LERP, dt / STEP);

    for (i = 0; i < items.length; i += 1) {
      it = items[i];
      if (!it.awake) {
        continue;
      }

      /* One rect per frame, and only while something is actually following — the same trade
         focus-scroll-pin.js makes. Reading it every frame rather than caching it on move is what
         keeps the image glued to the cursor while the page scrolls underneath it. */
      rect = it.host.getBoundingClientRect();

      /* The pointer can stop being over the table without a mouseleave — the commonest way is the
         page scrolling the table out from under a hand that never moved. The rect is already read,
         so the check is free, and it also covers any mouseleave a re-render swallowed. */
      if (
        it.on &&
        (it.px < rect.left || it.px > rect.right || it.py < rect.top || it.py > rect.bottom)
      ) {
        leave(it);
      }

      want = it.px - rect.left;
      it.side = sideFor(it, want);
      tx = clamp(want + it.side * (it.halfW + GAP), it.halfW, rect.width);
      ty = clamp(it.py - rect.top, it.halfH, rect.height);

      if (it.x === null) {
        /* First frame of an appearance: be where the cursor is. Flying in from the last position,
           or from the origin, is the one thing that makes this read as a widget rather than as a
           reaction to the pointer. */
        it.x = tx;
        it.y = ty;
      } else {
        it.x += (tx - it.x) * alpha;
        it.y += (ty - it.y) * alpha;
      }

      if (Math.abs(tx - it.x) < SNAP) {
        it.x = tx;
      } else {
        moving = true;
      }
      if (Math.abs(ty - it.y) < SNAP) {
        it.y = ty;
      } else {
        moving = true;
      }

      write(it);

      if (it.x === tx && it.y === ty) {
        /* Arrived. Nothing to do until the cursor moves or the page scrolls — both of which call
           wake() and schedule() — so the loop ends here rather than idling at 60fps. */
        it.awake = false;
      }
    }

    if (moving) {
      frame = window.requestAnimationFrame(update);
    } else {
      /* Reset the clock so the next start measures its first dt from the first frame, not from
         however long the page sat still. */
      last = 0;
    }
  }

  function show(it, pic) {
    if (it.pic === pic) {
      return;
    }
    if (it.pic) {
      it.pic.classList.remove('is-current');
    }
    it.pic = pic;
    if (pic) {
      pic.classList.add('is-current');
    }
  }

  function leave(it) {
    if (!it.on) {
      return;
    }
    it.on = false;
    it.leftAt = now();
    it.follow.classList.remove('is-on');
    show(it, null);
    /* The loop is NOT stopped here: a cursor flicked off the table leaves the box mid-flight, and
       letting it finish its glide while it fades is the whole reason it has weight. It settles and
       stops itself a few frames later. */
  }

  function enter(it) {
    if (it.on) {
      return;
    }
    it.on = true;
    it.halfW = it.follow.offsetWidth / 2;
    it.halfH = it.follow.offsetHeight / 2;
    /* Snap only if the previous appearance has finished fading out. Re-entering DURING the fade
       keeps the position it already has, so a cursor that clips the bottom of the table and comes
       straight back is caught by the box that is still there rather than teleporting a visible
       image across the component. */
    if (!it.leftAt || now() - it.leftAt > it.leaveMs) {
      place(it);
    }
    it.follow.classList.add('is-on');
  }

  function onMove(it, event) {
    var row;
    var key;
    var pic;

    /* Recorded before anything else: enter() places the box from these, in this same event. */
    it.px = event.clientX;
    it.py = event.clientY;

    /* The stylesheet owns the breakpoint: below it the follower is display:none, offsetParent is
       null and there is nothing to position. Asked here rather than from a resize listener, so
       there is no width written down in this file and no state to keep in sync. */
    if (!it.follow.offsetParent) {
      leave(it);
      return;
    }

    row = event.target.closest ? event.target.closest('tr') : null;
    /* A null row means the pointer is on a collapsed border between two rows for one event. Holding
       the last row through it is what stops a 1px flicker at every row boundary; mouseleave below
       is the only authority on actually leaving. */
    if (row) {
      key = row.getAttribute('data-pt-key');
      pic = key && Object.prototype.hasOwnProperty.call(it.pics, key) ? it.pics[key] : null;
      if (!pic) {
        /* A range with no image attached: hide rather than hold an empty frame. */
        leave(it);
        return;
      }
      enter(it);
      show(it, pic);
    }

    wake(it);
    schedule();
  }

  function bind(table) {
    var follow;
    var body;
    var nodes;
    var pics;
    var it;
    var i;
    var key;

    if (table.hasAttribute(BOUND)) {
      return;
    }
    table.setAttribute(BOUND, '');

    follow = table.querySelector('[data-pt-follow]');
    body = table.querySelector('.dc-ptable__table tbody');
    if (!follow || !body) {
      return;
    }

    pics = {};
    nodes = follow.querySelectorAll('[data-pt-pic]');
    for (i = 0; i < nodes.length; i += 1) {
      key = nodes[i].getAttribute('data-pt-pic');
      if (key) {
        pics[key] = nodes[i];
      }
    }
    if (!nodes.length) {
      return;
    }

    it = {
      host: table,
      follow: follow,
      body: body,
      pics: pics,
      pic: null,
      on: false,
      awake: false,
      leftAt: 0,
      leaveMs: readDuration(follow, '--dc-dur-fast', 160),
      px: 0,
      py: 0,
      x: null,
      y: null,
      side: -1,
      halfW: 0,
      halfH: 0,
      transform: '',
    };

    body.addEventListener(
      'mousemove',
      function (event) {
        onMove(it, event);
      },
      { passive: true }
    );
    body.addEventListener('mouseleave', function () {
      leave(it);
      schedule();
    });

    items.push(it);
  }

  function boot(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var nodes = root.querySelectorAll('.dc-ptable');
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      bind(nodes[i]);
    }
  }

  /* A re-render in the theme editor replaces the nodes; the old ones are detached and their
     listeners go with them, but the items array would keep them alive. Dropped here rather than in
     a shopify:section:unload handler, because that event does not fire for every path that swaps
     this markup out. */
  function prune() {
    var live = [];
    var i;
    for (i = 0; i < items.length; i += 1) {
      if (document.contains(items[i].host)) {
        live.push(items[i]);
      }
    }
    items = live;
  }

  function onScroll() {
    var i;
    var any = false;
    for (i = 0; i < items.length; i += 1) {
      if (items[i].on) {
        wake(items[i]);
        any = true;
      }
    }
    if (any) {
      schedule();
    }
  }

  boot(document);

  document.addEventListener('shopify:section:load', function (event) {
    prune();
    boot(event.target);
  });

  /* The page moving under a stationary cursor changes where the row is, not where the pointer is.
     theme:scroll is the smoothed bus Lenis drives; the native event is the fallback for the
     reduced-motion and no-Lenis cases — which this file never reaches, but the pair costs nothing
     and keeps the idiom identical to focus-scroll-pin.js. */
  document.addEventListener('theme:scroll', onScroll);
  window.addEventListener('scroll', onScroll, { passive: true });

  /* Alt-tabbing away with the cursor parked on a row would otherwise leave the image lit on a page
     nobody is pointing at. */
  window.addEventListener('blur', function () {
    var i;
    for (i = 0; i < items.length; i += 1) {
      leave(items[i]);
    }
    schedule();
  });
})();
